import React, { useState, useEffect, useMemo } from 'react';
import { resolveImage } from '../utils/image';
import { useNavigate, useParams } from 'react-router-dom';
import {
  FiArrowLeft,
  FiPhone,
  FiMail,
  FiMapPin,
  FiShare2,
  FiSearch,
  FiShoppingCart,
  FiX,
  FiStar,
} from 'react-icons/fi';
import { IoStorefrontOutline } from 'react-icons/io5';
import { ecommerceService } from '../services';
import { useCart } from '../contexts/CartContext';
import type { Store, Product, ProductCategory } from '../types';
import { LoaderComponent, EmptyState, Modal } from '../components/ui';
import ProductCard from '../components/ProductCard';
import CartDrawer from '../components/CartDrawer';

const StoreDetail: React.FC = () => {
  const navigate = useNavigate();
  const { storeId } = useParams<{ storeId: string }>();
  const { cart, cartTotal, cartSubtotal, cartIvaTotal, itemCount, updateQuantity, removeFromCart, clearCart } = useCart();

  const [store, setStore] = useState<Store | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quickViewProduct, setQuickViewProduct] = useState<Product | null>(null);
  const [cartOpen, setCartOpen] = useState(false);

  useEffect(() => {
    if (storeId) loadStoreData();
  }, [storeId]);

  useEffect(() => {
    if (storeId) loadProducts();
  }, [storeId, selectedCategory]);

  const loadStoreData = async () => {
    setLoading(true);
    try {
      const [storeData, categoriesData] = await Promise.all([
        ecommerceService.getStoreDetails(storeId!),
        ecommerceService.getStoreCategories(storeId!),
      ]);
      setStore(storeData);
      setCategories(categoriesData);
      sessionStorage.setItem('pageTitle', storeData.NOME || 'Loja');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar dados da loja');
    } finally {
      setLoading(false);
    }
  };

  const loadProducts = async () => {
    setProductsLoading(true);
    try {
      const data = selectedCategory
        ? await ecommerceService.getStoreProductsByCategory(storeId!, selectedCategory)
        : await ecommerceService.getStoreProducts(storeId!);
      setProducts(data);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setProductsLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return products;
    const q = searchQuery.toLowerCase();
    return products.filter(
      p => p.NOME.toLowerCase().includes(q) || p.DESCRICAO?.toLowerCase().includes(q)
    );
  }, [products, searchQuery]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: store?.NOME || 'Loja', url: window.location.href });
      } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(window.location.href);
    }
  };

  const logoSrc = resolveImage(store?.LOGO, store?.MIME_TYPE);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoaderComponent size="md" text="A carregar loja..." />
      </div>
    );
  }

  if (error || !store) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <button onClick={() => navigate('/ecommerce')} className="mb-6 flex items-center gap-2 text-gray-500 hover:text-gray-800 transition-colors">
          <FiArrowLeft /> Voltar
        </button>
        <EmptyState
          icon={<IoStorefrontOutline className="text-5xl" />}
          title="Loja não encontrada"
          description={error || 'A loja que procura não existe ou foi removida'}
          action={<button onClick={() => navigate('/ecommerce')} className="mt-4 px-6 py-2 bg-pagali-purple text-white rounded-full">Ver todas as lojas</button>}
        />
      </div>
    );
  }

  const isDraft = (store as any).draft === true || (store as any).ESTADO === 'R';

  return (
    <div className="min-h-screen bg-gray-50 pb-28">

      {/* ── Draft banner — visível apenas para admin/dono ─────────── */}
      {isDraft && (
        <div className="bg-amber-400 text-amber-900 text-xs font-semibold text-center py-2 px-4 flex items-center justify-center gap-2">
          <span>⚠️</span>
          <span>Loja em rascunho — apenas visível para si. Active a loja para a tornar pública.</span>
        </div>
      )}

      {/* ── Hero Banner ─────────────────────────────────────────── */}
      <div className="relative bg-gradient-to-br from-gray-900 via-slate-800 to-pagali-purple/80 overflow-hidden">
        {/* decorative circles */}
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-pagali-purple/20 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-8 -left-8 w-48 h-48 rounded-full bg-white/5 blur-2xl pointer-events-none" />

        {/* top bar */}
        <div className="relative flex items-center justify-between px-4 pt-4 pb-0">
          <button
            onClick={() => navigate('/ecommerce')}
            className="flex items-center gap-2 text-white/80 hover:text-white transition-colors text-sm font-medium"
          >
            <FiArrowLeft />
            <span>Pagali Shop</span>
          </button>
          <button
            onClick={handleShare}
            className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          >
            <FiShare2 />
          </button>
        </div>

        {/* store identity */}
        <div className="relative px-4 py-6 flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-white/10 border-2 border-white/20 overflow-hidden flex-shrink-0 shadow-xl backdrop-blur-sm">
            {logoSrc ? (
              <img src={logoSrc} alt={store.NOME} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <IoStorefrontOutline className="text-3xl text-white/60" />
              </div>
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-white leading-tight truncate">{store.NOME}</h1>
            {store.CATEGORIA_NOME && (
              <span className="mt-1.5 inline-block px-3 py-0.5 bg-white/15 text-white/90 text-xs font-medium rounded-full border border-white/20">
                {store.CATEGORIA_NOME}
              </span>
            )}
            {store.DESCRICAO && (
              <p className="mt-2 text-white/60 text-sm line-clamp-2">{store.DESCRICAO}</p>
            )}
          </div>
        </div>

        {/* contact strip */}
        {(store.TELEFONE || store.EMAIL || store.ENDERECO) && (
          <div className="relative px-4 pb-5 flex flex-wrap gap-x-5 gap-y-1">
            {store.TELEFONE && (
              <a href={`tel:${store.TELEFONE}`} className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs transition-colors">
                <FiPhone className="text-white/40" />{store.TELEFONE}
              </a>
            )}
            {store.EMAIL && (
              <a href={`mailto:${store.EMAIL}`} className="flex items-center gap-1.5 text-white/60 hover:text-white text-xs transition-colors">
                <FiMail className="text-white/40" />{store.EMAIL}
              </a>
            )}
            {store.ENDERECO && (
              <span className="flex items-center gap-1.5 text-white/60 text-xs">
                <FiMapPin className="text-white/40" />{store.ENDERECO}
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="max-w-screen-xl mx-auto px-4">

        {/* Category Pills */}
        {categories.length > 0 && (
          <div className="pt-5 pb-1">
            <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
              <button
                onClick={() => setSelectedCategory(null)}
                className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  !selectedCategory
                    ? 'bg-pagali-purple text-white shadow-md shadow-pagali-purple/30'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-pagali-purple hover:text-pagali-purple'
                }`}
              >
                Todos
              </button>
              {categories.map(cat => (
                <button
                  key={cat.ID}
                  onClick={() => setSelectedCategory(selectedCategory === cat.ID ? null : cat.ID)}
                  className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    selectedCategory === cat.ID
                      ? 'bg-pagali-purple text-white shadow-md shadow-pagali-purple/30'
                      : 'bg-white text-gray-600 border border-gray-200 hover:border-pagali-purple hover:text-pagali-purple'
                  }`}
                >
                  {cat.NOME}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative mt-4">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <FiX />
            </button>
          )}
          <input
            type="text"
            placeholder="Pesquisar produtos..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-10 py-3 bg-white rounded-2xl border border-gray-200 focus:border-pagali-purple focus:ring-2 focus:ring-pagali-purple/15 outline-none transition-all text-sm shadow-sm"
          />
        </div>

        {/* Product count */}
        <div className="mt-5 mb-3 flex items-center justify-between">
          <p className="text-sm text-gray-500 font-medium">
            {productsLoading ? '...' : `${filteredProducts.length} produto${filteredProducts.length !== 1 ? 's' : ''}`}
          </p>
          {selectedCategory && (
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-xs text-pagali-purple hover:underline"
            >
              Limpar filtro
            </button>
          )}
        </div>

        {/* Products Grid */}
        {productsLoading ? (
          <div className="py-16 flex justify-center">
            <LoaderComponent size="md" text="A carregar produtos..." />
          </div>
        ) : filteredProducts.length === 0 ? (
          <EmptyState
            icon={<IoStorefrontOutline className="text-5xl" />}
            title="Sem produtos"
            description={searchQuery ? 'Nenhum produto encontrado' : 'Esta loja ainda não tem produtos disponíveis'}
          />
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 sm:gap-4">
            {filteredProducts.map(product => (
              <ProductCard
                key={product.ID}
                product={product}
                storeId={storeId}
                onQuickView={setQuickViewProduct}
              />
            ))}
          </div>
        )}
      </div>

      {/* ── Floating Cart Bar ───────────────────────────────────── */}
      {itemCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 px-4 pb-6 pointer-events-none">
          <button
            onClick={() => setCartOpen(true)}
            className="pointer-events-auto w-full max-w-lg mx-auto flex items-center justify-between bg-gray-900 text-white rounded-2xl px-5 py-4 shadow-2xl hover:bg-gray-800 transition-colors"
          >
            <div className="flex items-center gap-3">
              <div className="relative">
                <FiShoppingCart className="text-xl" />
                <span className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-pagali-purple text-white text-[10px] font-bold flex items-center justify-center">
                  {itemCount}
                </span>
              </div>
              <span className="font-medium">Ver carrinho</span>
            </div>
            <span className="font-bold text-lg">{Math.round(cartTotal).toLocaleString('pt-CV')} ECV</span>
          </button>
        </div>
      )}

      {/* Cart Drawer */}
      <CartDrawer isOpen={cartOpen} onClose={() => setCartOpen(false)} shippingFee={store?.taxaEntrega ?? 0} />

      {/* ── Quick View Modal ────────────────────────────────────── */}
      <Modal
        isOpen={!!quickViewProduct}
        onClose={() => setQuickViewProduct(null)}
        title={quickViewProduct?.NOME || 'Produto'}
        size="lg"
      >
        {quickViewProduct && (
          <div className="flex flex-col md:flex-row gap-6">
            <div className="w-full md:w-1/2 aspect-[3/4] bg-gray-100 rounded-2xl overflow-hidden">
              {resolveImage(quickViewProduct.IMAGEM, quickViewProduct.MIME_TYPE) ? (
                <img
                  src={resolveImage(quickViewProduct.IMAGEM, quickViewProduct.MIME_TYPE)!}
                  alt={quickViewProduct.NOME}
                  className="w-full h-full object-cover object-top"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gray-100">
                  <IoStorefrontOutline className="text-6xl text-gray-300" />
                </div>
              )}
            </div>
            <div className="w-full md:w-1/2 flex flex-col">
              <h2 className="text-xl font-bold text-gray-900">{quickViewProduct.NOME}</h2>
              {quickViewProduct.DESCRICAO && (
                <p className="mt-2 text-gray-500 text-sm leading-relaxed">{quickViewProduct.DESCRICAO}</p>
              )}
              <div className="mt-4 flex items-baseline gap-3">
                <span className="text-2xl font-bold text-gray-900">
                  {(quickViewProduct.PRECO_PROMOCIONAL || quickViewProduct.PRECO || 0).toLocaleString('pt-CV')} ECV
                </span>
                {quickViewProduct.PRECO_PROMOCIONAL && (
                  <span className="text-base text-gray-400 line-through">
                    {(quickViewProduct.PRECO || 0).toLocaleString('pt-CV')} ECV
                  </span>
                )}
              </div>
              <div className="mt-2">
                {quickViewProduct.STOCK === 0 ? (
                  <span className="text-xs text-red-500 font-medium">Esgotado</span>
                ) : (
                  <span className="text-xs text-green-600 font-medium">Em stock</span>
                )}
              </div>
              <div className="mt-auto pt-6">
                <ProductCard product={quickViewProduct} variant="compact" />
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default StoreDetail;
